// AuthGate — root-level auth orchestration. On first mount, attempts a
// /v1/auth/refresh. On success, lets children render. On failure, redirects
// to /login. bootStatus is DERIVED from accessToken + probing — there is no
// stored phase that can drift from the live token.
//
// Why we no longer gate on `hadSessionHint()`:
//   The non-HttpOnly hint cookie is intended to skip a doomed POST /v1/auth/
//   refresh on first-ever visit. In practice the hint can be missing while
//   the refresh cookie is valid — browser privacy modes (Brave Strict, Safari
//   ITP, Firefox ETP, Chrome's third-party-cookie partitioning) sometimes
//   strip JS-readable cookies but keep HttpOnly ones. An edge proxy can also
//   drop or rename Set-Cookie headers in transit. When that happens the old
//   code would never probe and would bounce the user to /login forever even
//   though their session was still valid — exactly the GCP regression we hit.
//
//   The probe itself is cheap and idempotent (one Redis check + one DB row
//   lookup, no rotation unless the cookie is valid). A failed probe is a
//   single 401 in the console for first-time visitors on protected paths —
//   the cost we trade for robustness. On public paths (/login, /, marketing)
//   we still skip the probe when there is no hint so true first-time visitors
//   see no spurious 401.
//
// Why the redirect now waits for the probe:
//   The previous version fired two useEffects in the same flush: effect 1
//   set probing=true, effect 2 read the closure-captured bootStatus (still
//   'needs_login' at that snapshot) and redirected to /login. The redirect
//   raced ahead of the probe and the user was bounced even when the probe
//   later succeeded. We now derive `probeSettled` and never redirect until
//   it's true.

import { bootProbe } from '@/lib/api';
import { isPublicPath } from '@/lib/auth-guards';
import { selectBootStatus, useAuth } from '@/state/auth';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { type ReactNode, useEffect, useRef, useState } from 'react';

// Read the server-set session hint cookie. Cheap optimization to skip the
// probe for true first-time visitors on public paths — never used to GATE
// the probe on protected paths.
function hadSessionHint(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie.split(/;\s*/).some((c) => c === 'eid_had_session=1');
}

export function AuthGate({ children }: { children: ReactNode }) {
  const bootStatus = useAuth(selectBootStatus);
  const setProbing = useAuth((s) => s.setProbing);
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Once-only guard against StrictMode double-fire and remounts.
  const probeStartedRef = useRef(false);
  // The probe has run to completion (success OR failure). Until this flips,
  // we never redirect — a redirect before this is a guess that there's no
  // session, and the guess is wrong whenever the refresh cookie is valid.
  const [probeSettled, setProbeSettled] = useState(false);
  // Capture the initial pathname so the public-path decision is fixed at
  // mount time. Routing changes after mount don't re-trigger the probe.
  const initialPathRef = useRef<string>(pathname);

  useEffect(() => {
    if (probeStartedRef.current) return;
    probeStartedRef.current = true;

    // Public path + no hint cookie → skip the probe. Pure UX optimization
    // to avoid a 401 in the console for first-time visitors. We still flip
    // probeSettled so the redirect-gate doesn't hang.
    if (isPublicPath(initialPathRef.current) && !hadSessionHint()) {
      setProbeSettled(true);
      return;
    }

    setProbing(true);
    bootProbe().finally(() => {
      setProbing(false);
      setProbeSettled(true);
    });
  }, [setProbing]);

  // Redirect to /login only after the probe has settled and there's still
  // no access token. Until then, render the spinner. This is the load-
  // bearing fix for the redirect-race.
  useEffect(() => {
    if (!probeSettled) return;
    if (bootStatus !== 'needs_login') return;
    if (isPublicPath(pathname)) return;
    void navigate({ to: '/login', replace: true });
  }, [bootStatus, pathname, probeSettled, navigate]);

  // Spinner while the probe is in flight OR has not yet been attempted.
  // The latter happens for exactly one render tick between mount and the
  // first useEffect — without this branch the page flashes the
  // "needs_login" rendering before the probe starts.
  if (!probeSettled && bootStatus !== 'ready') {
    return (
      <div
        className="eid"
        style={{
          height: '100vh',
          width: '100vw',
          background: 'var(--bg-base)',
          color: 'var(--fg-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <div className="progress" />
        <span className="meta upper mono">resolving session…</span>
      </div>
    );
  }

  // Probe settled, no session, not on a public path — render nothing while
  // the redirect effect runs. Prevents a flash of protected-page chrome.
  if (bootStatus === 'needs_login' && !isPublicPath(pathname)) return null;

  return <>{children}</>;
}
